import { Injectable } from '@angular/core';
import { PublicidadClickContext } from '../../models/publicidad/publicidad.model';
import { environment } from '../../../../environments/environment';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private static readonly PANTALLA_PATH_PREFIX = '/pantallas';
  private static readonly PANTALLA_TITLE_PREFIX = '[PANTALLA] ';
  private static readonly DEBUG_QUERY_PARAM = 'ga_debug';
  private static readonly GTAG_BASE_URL = 'https://www.googletagmanager.com/gtag/js';

  private readonly config = environment.GA4;
  private ready = false;
  private lastPath = '/';

  /**
   * Carga gtag.js e inicializa GA4. Idempotente: se puede llamar varias veces.
   * Si GA4 esta deshabilitado o el Measurement ID no es valido, no hace nada.
   */
  public initialize(): void {
    if (this.ready || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (!this.config?.enabled) {
      return;
    }

    const measurementId = (this.config.measurementId ?? '').trim();
    if (!measurementId || measurementId.includes('XXXX')) {
      console.warn('[Analytics] Measurement ID no configurado. GA4 queda deshabilitado.');
      return;
    }

    this.ready = true;

    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function gtag() {
      // gtag.js espera el objeto "arguments" crudo: no reemplazar por rest params ni por un array.
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      // Los pageviews los manda trackPageView() en cada NavigationEnd.
      send_page_view: false,
      debug_mode: this.isDebugMode()
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `${AnalyticsService.GTAG_BASE_URL}?id=${encodeURIComponent(measurementId)}`;
    script.setAttribute('data-analytics', 'ga4');
    script.onerror = () => {
      console.warn('[Analytics] No se pudo cargar gtag.js (bloqueador o sin conexion).');
    };
    document.head.appendChild(script);
  }

  /** Envia un page_view manual. `url` es el `urlAfterRedirects` del router. */
  public trackPageView(url: string): void {
    const path = this.toPath(url);
    const pathname = this.toPathname(path);

    // Se guarda para poder usarla como location_id en los eventos de publicidad.
    this.lastPath = pathname;

    this.trackEvent('page_view', {
      page_title: this.buildPageTitle(pathname),
      page_location: this.buildPageLocation(path)
    });
  }

  /** Punto unico de salida hacia GA4. Nunca lanza. */
  public trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
    if (!this.ready || typeof window === 'undefined' || !window.gtag) {
      return;
    }

    try {
      window.gtag('event', eventName, params);
    } catch {
      // Analytics jamas debe romper la aplicacion.
    }
  }

  /** Click sobre una publicidad. Evento estandar de GA4 para publicidad interna. */
  public trackSeleccionPublicidad(publicidadID: number, contexto?: PublicidadClickContext): void {
    this.trackEvent('select_promotion', this.buildPromotionParams(publicidadID, contexto));
  }

  private buildPromotionParams(
    publicidadID: number,
    contexto?: PublicidadClickContext
  ): Record<string, unknown> {
    const promotionId = String(publicidadID);
    const promotionName = this.resolvePromotionName(publicidadID, contexto);
    const creativeSlot = contexto?.codigo ?? 'DESCONOCIDO';
    const creativeName = (contexto?.tipoPublicidad ?? '').trim() || creativeSlot;
    const locationId = this.lastPath || '/';

    const promotion = {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
      creative_name: creativeName,
      location_id: locationId
    };

    return {
      ...promotion,
      // El informe nativo de Promociones cuenta desde el array items.
      items: [
        {
          ...promotion,
          item_id: `publicidad_${promotionId}`,
          item_name: promotionName,
          index: contexto?.ordenVisualizacion ?? 0
        }
      ]
    };
  }

  private resolvePromotionName(publicidadID: number, contexto?: PublicidadClickContext): string {
    const nombreArchivo = (contexto?.nombreArchivo ?? '').trim();
    if (!nombreArchivo) {
      return `Publicidad ${publicidadID}`;
    }

    // "biogenesis-banner.png" -> "biogenesis-banner"
    const sinExtension = nombreArchivo.replace(/\.[a-z0-9]+$/i, '').trim();
    return sinExtension || nombreArchivo;
  }

  private buildPageTitle(pathname: string): string {
    const documentTitle = typeof document !== 'undefined' ? document.title.trim() : '';
    const title = documentTitle || 'Expo Genetic';

    return this.isPantallaRoute(pathname)
      ? `${AnalyticsService.PANTALLA_TITLE_PREFIX}${title}`
      : title;
  }

  private buildPageLocation(path: string): string {
    if (typeof window === 'undefined') {
      return path;
    }

    return `${window.location.origin}${path}`;
  }

  private isPantallaRoute(pathname: string): boolean {
    return pathname.startsWith(AnalyticsService.PANTALLA_PATH_PREFIX);
  }

  private toPath(url: string): string {
    const normalized = typeof url === 'string' ? url.trim() : '';
    if (!normalized) {
      return '/';
    }

    return normalized.startsWith('/') ? normalized : `/${normalized}`;
  }

  private toPathname(path: string): string {
    return path.split('?')[0].split('#')[0];
  }

  private isDebugMode(): boolean {
    if (this.config.debugMode) {
      return true;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      return params.get(AnalyticsService.DEBUG_QUERY_PARAM) === '1';
    } catch {
      return false;
    }
  }
}